import { Address } from "viem"

import { GOLDSKY_MEZO_SUBGRAPH_URL } from "../constants"

/**
 * Represents the response data returned form the Goldsky Subgraph from query
 * that finds all stakes for a given staker.
 */
type StakeDataResponse = {
  data: {
    stakes: {
      /**
       * Unique identifier of the VeBTC NFT token.
       */
      id: string

      /**
       * Staked amount.
       */
      amount: string

      /**
       * Number of seconds to lock tokens for.
       */
      lockDuration: string

      /**
       * Timestamp of the stake creation.
       */
      initializedAt: number

      /**
       * Unlock date as a timestamp.
       */
      unlockAt: string

      /**
       * Determines wether the stake is locked permanently or not.
       */
      isPermanent: boolean

      /**
       * Permanent lock amount of a given stake.
       */
      permanentLockAmount: string
    }[]
  }
}

const buildGetStakesQuery = (staker: Address) => `
  query {
    stakes(
        where: {staker: "${staker.toLocaleLowerCase()}"}
        orderBy: initializedAt
        orderDirection: asc
    ) {
    id
    amount
    lockDuration
    initializedAt
    unlockAt
    isPermanent
    permanentLockAmount
  }
}
`

async function getStakesByStaker(staker: Address) {
  const response = await fetch(GOLDSKY_MEZO_SUBGRAPH_URL, {
    method: "POST",
    body: JSON.stringify({ query: buildGetStakesQuery(staker) }),
    headers: { "Content-Type": "application/json" },
  })

  if (!response.ok) {
    throw new Error(`Could not get stakes by staker: ${response.status}`)
  }

  const responseData = (await response.json()) as StakeDataResponse

  return responseData.data.stakes
}

type StakingMetricsDataResponse = {
  data: {
    stakingMetrics: {
      /**
       * Total staked BTC.
       */
      totalStaked: string

      /**
       * Number of stakers.
       */
      stakers: string
    }
  }
}

const getStakingStatsQuery = `
  query {
    stakingMetrics(id: "staking-metrics") {
      totalStaked
      stakers
    }
  }
`

async function getStakingMetrics() {
  const response = await fetch(GOLDSKY_MEZO_SUBGRAPH_URL, {
    method: "POST",
    body: JSON.stringify({ query: getStakingStatsQuery }),
    headers: { "Content-Type": "application/json" },
  })

  if (!response.ok) {
    throw new Error("Could not get staking metrics")
  }

  const responseData = (await response.json()) as StakingMetricsDataResponse

  return responseData.data.stakingMetrics
}

export default {
  getStakesByStaker,
  getStakingMetrics,
}
